import moment from 'moment';
import { LogSearchAction } from '../actions/logsearch';
import { APIError } from '../APIError';
import { getObjVal, sortURLSearchParams } from '../constants';
import {
  ActiveSearch,
  Export,
  Search,
  SearchRequest,
  SearchResults,
  Store,
} from '../types';

export type State = {
  readonly earliest: moment.Moment | null;
  readonly error: APIError | null;
  readonly filters: URLSearchParams | null;
  readonly listError: APIError | null;
  readonly listLoading: boolean;
  readonly activeSearch: ActiveSearch;
  readonly searches: Array<Search> | null;
  readonly resultPage: SearchResults;
};

export const LOGSEARCH_ACTIVITY_FILTER = {
  USER: 'user',
  FILTER: 'filter',
};

export const LOGSEARCH_ACTIVITY_FILTER_KEYS = [
  LOGSEARCH_ACTIVITY_FILTER.USER,
  LOGSEARCH_ACTIVITY_FILTER.FILTER,
];

export const getBlankSearchRequest = (): SearchRequest => ({
  caseSensitive: true,
  end: moment(),
  query: '',
  start: moment().subtract(1, 'hour'),
  logSourcesTags: {},
});

export const getBlankSearch = (): Search => ({
  id: '',
  request: getBlankSearchRequest(),
  failed: false,
  date: moment(),
  user: '',
  count: 0,
  availableCount: 0,
  percent: 0,
  size: 0,
  searchExport: null,
});

export const INITIAL_LOG_SEARCH_STATE: State = {
  earliest: null,
  error: null,
  filters: null,
  listError: null,
  listLoading: false,
  activeSearch: {
    search: getBlankSearch(),
    histogram: null,
  },
  searches: null,
  resultPage: { pageNum: 0, loading: false, results: null },
};

const logSearchReducer = (
  state: State = INITIAL_LOG_SEARCH_STATE,
  action: LogSearchAction,
): State => {
  switch (action.type) {
    case 'start_new_search':
      return {
        ...state,
        activeSearch: {
          search: action.clearRequest
            ? getBlankSearch()
            : {
                ...getBlankSearch(),
                request: state.activeSearch.search.request,
              },
          histogram: null,
        },
        resultPage: INITIAL_LOG_SEARCH_STATE.resultPage,
        error: null,
      };

    case 'set_log_search_id':
      return {
        ...state,
        activeSearch: {
          search: {
            ...state.activeSearch.search,
            id: action.searchId,
          },
          histogram: state.activeSearch.histogram,
        },
      };

    case 'set_log_search':
      return {
        ...state,
        activeSearch: {
          search: action.search,
          histogram: action.histogram,
        },
        resultPage:
          action.search.percent >= 100 && action.search.count === 0
            ? { pageNum: 0, loading: false, results: [] }
            : state.resultPage,
      };

    case 'set_log_search_export':
      return {
        ...state,
        activeSearch: {
          ...state.activeSearch,
          search: {
            ...state.activeSearch.search,
            searchExport: action.export,
          },
        },
      };

    case 'set_log_search_result_page':
      if (state.activeSearch.search.id !== action.searchId) {
        return state;
      }

      return {
        ...state,
        resultPage: action.resultPage,
      };

    case 'set_log_searches':
      return {
        ...state,
        searches: action.searches,
        listLoading: false,
        error: null,
      };

    case 'set_log_searches_loading':
      return {
        ...state,
        listLoading: true,
      };

    case 'set_log_searches_filters':
      const filters = sortURLSearchParams(
        action.filters,
        LOGSEARCH_ACTIVITY_FILTER_KEYS,
      );

      if ((state.filters || {}).toString() !== filters.toString()) {
        return {
          ...state,
          filters,
        };
      }

      return state;

    case 'set_log_search_list_error':
      return {
        ...state,
        listError: action.error,
        listLoading: false,
      };

    case 'set_log_search_error':
      return {
        ...state,
        error: action.error,
      };

    case 'set_log_earliest_record':
      return {
        ...state,
        earliest: action.earliest,
      };

    default:
      return state;
  }
};

export default logSearchReducer;

// ------------
// Selectors
// -------------

export const logSearchSelector = {
  getEarliestRecord: (store: Store): moment.Moment | null =>
    store.logSearch.earliest,
  getExport: (store: Store): Export | null =>
    store.logSearch.activeSearch.search.searchExport,
  getSearch: (store: Store): ActiveSearch => store.logSearch.activeSearch,
  getSearchError: (store: Store): APIError | null => store.logSearch.error,
  getSearchesList: (store: Store): Array<Search> | null =>
    store.logSearch.searches,
  getSearchesListError: (store: Store): APIError | null =>
    store.logSearch.listError,
  getSearchesListFilters: (store: Store): URLSearchParams | null =>
    store.logSearch.filters,
  getSearchId: (store: Store): string =>
    getObjVal(store, 'logSearch.activeSearch.search.id', ''),
  getSearchResultPage: (store: Store): SearchResults =>
    store.logSearch.resultPage,
  isListLoading: (store: Store): boolean => store.logSearch.listLoading,
};
